import React, { useMemo } from 'react';

interface MatrixRow {
  customerId: string;
  customerName: string;
  data: { groupId: string; groupName: string; count: number }[];
}

interface CustomerGroupMatrixTableProps {
  matrix: MatrixRow[];
  groups: { id: string; name: string }[];
  maxCount: number;
  onCellClick?: (customerId: string, groupId: string, customerName: string, groupName: string) => void;
}

// Get cell background color based on value (heatmap)
const getCellColor = (count: number, maxCount: number): string => {
  if (count === 0) return 'bg-gray-50';
  const intensity = count / maxCount;
  if (intensity <= 0.25) return 'bg-blue-100 text-blue-800';
  if (intensity <= 0.5) return 'bg-yellow-200 text-yellow-900';
  if (intensity <= 0.75) return 'bg-orange-300 text-orange-900';
  return 'bg-red-400 text-white';
};

const CustomerGroupMatrixTable: React.FC<CustomerGroupMatrixTableProps> = ({
  matrix,
  groups,
  maxCount,
  onCellClick,
}) => {
  // Transform matrix data: rows = groups, columns = customers
  const transformedData = useMemo(() => {
    // Get unique customers from matrix
    const customers = matrix.map(row => ({
      id: row.customerId,
      name: row.customerName,
    }));

    // Build rows by group
    const rows = groups.map(group => {
      const data = customers.map(customer => {
        const customerRow = matrix.find(m => m.customerId === customer.id);
        const groupData = customerRow?.data.find(d => d.groupId === group.id);
        return {
          customerId: customer.id,
          customerName: customer.name,
          count: groupData?.count || 0,
        };
      });

      const rowTotal = data.reduce((sum, cell) => sum + cell.count, 0);

      return {
        groupId: group.id,
        groupName: group.name,
        data,
        rowTotal,
      };
    });

    // Calculate column totals
    const columnTotals = customers.map(customer => {
      const total = rows.reduce((sum, row) => {
        const cell = row.data.find(d => d.customerId === customer.id);
        return sum + (cell?.count || 0);
      }, 0);
      return {
        customerId: customer.id,
        customerName: customer.name,
        total,
      };
    });

    // Grand total
    const grandTotal = columnTotals.reduce((sum, col) => sum + col.total, 0);

    return { customers, rows, columnTotals, grandTotal };
  }, [matrix, groups]);

  if (matrix.length === 0 || groups.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Customer vs Assignment Group Distribution
        </h3>
        <div className="h-32 flex items-center justify-center text-gray-500">
          No data available for the selected filters
        </div>
      </div>
    );
  }

  const { customers, rows, columnTotals, grandTotal } = transformedData;

  return (
    <div id="chart-heatmap" className="bg-white rounded-lg shadow">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-700 rounded-t-lg">
        <h3 className="text-lg font-medium text-white text-center">
          Customer vs Assignment Group Distribution
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-100 z-10 border-r border-gray-300">
                Assignment Group
              </th>
              {customers.map((customer) => (
                <th
                  key={customer.id}
                  className="px-2 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-200"
                  title={customer.name}
                >
                  {customer.name.length > 15
                    ? customer.name.substring(0, 15) + '...'
                    : customer.name}
                </th>
              ))}
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider bg-gray-200">
                Count
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row) => (
              <tr key={row.groupId} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 sticky left-0 bg-white z-10 border-r border-gray-300">
                  {row.groupName}
                </td>
                {row.data.map((cell) => (
                  <td
                    key={`${row.groupId}-${cell.customerId}`}
                    className={`px-2 py-2 text-center text-sm font-medium border-r border-gray-200 ${getCellColor(
                      cell.count,
                      maxCount
                    )} ${cell.count > 0 ? 'cursor-pointer hover:opacity-80' : ''}`}
                    onClick={() => {
                      if (cell.count > 0 && onCellClick) {
                        onCellClick(cell.customerId, row.groupId, cell.customerName, row.groupName);
                      }
                    }}
                    title={cell.count > 0 ? `Click to view ${cell.count} tickets for ${cell.customerName} - ${row.groupName}` : undefined}
                  >
                    {cell.count > 0 ? (
                      <span className="underline decoration-dotted">{cell.count}</span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                ))}
                <td className="px-3 py-2 text-center text-sm font-bold text-gray-900 bg-gray-100">
                  {row.rowTotal}
                </td>
              </tr>
            ))}

            {/* Column totals row */}
            <tr className="bg-gray-100 font-bold">
              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 sticky left-0 bg-gray-100 z-10 border-r border-gray-300">
                Count
              </td>
              {columnTotals.map((col) => (
                <td
                  key={`total-${col.customerId}`}
                  className="px-2 py-2 text-center text-sm text-gray-900 border-r border-gray-200"
                >
                  {col.total}
                </td>
              ))}
              <td className="px-3 py-2 text-center text-sm font-bold text-white bg-gray-700">
                {grandTotal}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomerGroupMatrixTable;
